import type { AddressDTO, ConfigurationFlowId, PaymentMethod, ResolvedCatalogue } from 'models/purchaseFlowRevamp'
import type { NextStepValidationError } from 'models/purchaseFlow/nextStepValidationError'
import { cartProductKinds } from 'dataQueries/purchaseFlowRevampProductCodes'

import type { PfStoreState } from '../store'
import { selectBillingStepErrorsFor, selectBillingStepValidFor } from './billing'
import { selectConfigurationStepErrors, selectConfigurationStepValid } from './configuration'
import { selectInstructionsStepErrors, selectInstructionsStepValid } from './instructions'

type SummaryCatalogue = {
  shellProducts: Array<{
    id: string
    requiresConfiguration: boolean
    categoryId: string
    configurationFlows?: ConfigurationFlowId[]
    productType?: string
    kind?: string
    variants?: Array<{ id: string; configurationFlows?: ConfigurationFlowId[] }>
  }>
}

/**
 * The "Place order" gate: a non-empty cart that passes the Configuration, Instructions and Billing
 * gates, each given the same inputs its own step uses — so a step that lets the client through never
 * leaves Summary refusing.
 */
export const selectSummaryStepValid =
  (
    catalogue?: SummaryCatalogue,
    allowFallback = false,
    instructionsVisible?: boolean,
    defaultPaymentMethod?: PaymentMethod,
    /** Same reason the 4th argument exists: a default that opens
     *  Billing's own gate must open this one too, or Proceed works and Summary
     *  refuses. */
    defaultInvoicingAddress?: AddressDTO
  ) =>
    (state: PfStoreState): boolean => {
      if (state.items.length === 0) return false
      if (!catalogue) return false

      if (!selectConfigurationStepValid(catalogue, allowFallback)(state)) return false
      if (!selectInstructionsStepValid(catalogue, instructionsVisible)(state)) return false
      if (!selectBillingStepValidFor(defaultPaymentMethod, cartProductKinds(state.items, catalogue as ResolvedCatalogue), defaultInvoicingAddress)(state)) return false

      return true
    }

/**
 * Why `selectSummaryStepValid` refuses, as `validation_errors` codes: the codes of the first step in the chain
 * that is not complete, as legacy reports them. Takes the gate's arguments, so change the two together.
 */
export const selectSummaryStepErrors =
  (
    catalogue?: SummaryCatalogue,
    allowFallback = false,
    instructionsVisible?: boolean,
    defaultPaymentMethod?: PaymentMethod,
    defaultInvoicingAddress?: AddressDTO
  ) =>
    (state: PfStoreState): NextStepValidationError[] => {
      if (state.items.length === 0) return ['product_missing']
      if (!catalogue) return ['step_incomplete']

      const configuration = selectConfigurationStepErrors(catalogue, allowFallback)(state)
      if (configuration.length > 0) return configuration
      const instructions = selectInstructionsStepErrors(catalogue, instructionsVisible)(state)
      if (instructions.length > 0) return instructions
      return selectBillingStepErrorsFor(defaultPaymentMethod, cartProductKinds(state.items, catalogue as ResolvedCatalogue), defaultInvoicingAddress)(state)
    }
